import PocketBase, { RecordAuthResponse } from "pocketbase";
import { TypedPocketBase, UsersResponse } from "@/types/pocketbase-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import Client from "pocketbase";
import { UsersInfo } from "@/types/expanded-types";
import { jwtDecode } from "jwt-decode";
import { useInterval } from "usehooks-ts";

const fiveMinutesInMs = 1 * 60 * 1000;
const twoMinutesInMs = 2 * 60 * 1000;

type PocketbaseContextProps = {
  children: React.ReactNode;
};

type PocketBaseContext = {
  register: (
    email: string,
    password: string,
    passwordConfirm: string,
  ) => Promise<UsersResponse<unknown>>;
  emailConfirmation: (email: string) => Promise<boolean>;
  login: (
    email: string,
    password: string,
  ) => Promise<RecordAuthResponse<UsersResponse<unknown>>>;
  logout: () => void;
  recoverPassword: (email: string) => Promise<boolean>;
  changePassword: (
    token: string,
    password: string,
    passwordConfirm: string,
  ) => Promise<boolean>;
  user: Partial<UsersInfo> | null;
  token: string;
  session: string;
  pb: Client;
};

const PocketContext = createContext({} as PocketBaseContext);

// Context component with custom logic to handle auth
export const PocketProvider = ({ children }: PocketbaseContextProps) => {
  const pb = useMemo(
    () =>
      new PocketBase(
        import.meta.env.VITE_REACT_APP_POCKETBASE_URL,
      ) as TypedPocketBase,
    [],
  );

  const [token, setToken] = useState(pb.authStore.token);
  const [session, setSession] = useState(
    pb.authStore.model?.last_session as string,
  );
  const [user, setUser] = useState(pb.authStore.model as Partial<UsersInfo>);

  // Save user and token on auth change
  useEffect(() => {
    return pb.authStore.onChange((token, model) => {
      console.log("Auth store changed. New token:", token);
      console.log("Auth store changed. New user:", model);
      setToken(token);
      setSession(model?.last_session as string);

      const userParsed = model as Partial<UsersInfo>;
      if (!userParsed) {
        setUser(userParsed);
        return;
      }
      setUser(userParsed);
    });
  }, []);

  // Register, login, logout
  const register = useCallback(
    async (email: string, password: string, passwordConfirm: string) => {
      return await pb.collection("users").create({
        email,
        password,
        passwordConfirm,
        emailVisibility: true,
      });
    },
    [],
  );

  // Register, login, logout
  const emailConfirmation = useCallback(async (email: string) => {
    return await pb.collection("users").requestVerification(email);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const userLogin = await pb
      .collection("users")
      .authWithPassword(email, password);

    return userLogin;
  }, []);

  const logout = useCallback(() => {
    pb.authStore.clear();
  }, []);

  // To request password recovery email
  const recoverPassword = useCallback(async (email: string) => {
    const res = await pb.collection("users").requestPasswordReset(email);
    return res;
  }, []);

  //
  const changePassword = useCallback(
    async (token: string, password: string, passwordConfirm: string) => {
      const res = await pb
        .collection("users")
        .confirmPasswordReset(token, password, passwordConfirm);

      return res;
    },
    [],
  );

  // refresh token or refresh sesion
  const refreshSession = useCallback(async () => {
    if (!pb.authStore.isValid) return;
    const decoded = jwtDecode(token);
    if (!decoded.exp) throw new Error("Token has no expiration");

    const tokenExpiration = decoded.exp;
    const expirationWithBuffer = (decoded.exp + fiveMinutesInMs) / 1000;
    if (tokenExpiration < expirationWithBuffer) {
      await pb.collection("users").authRefresh();
    }
  }, [token]);

  // Schedule refresh token or session validation each 2 minutes
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  useInterval(refreshSession, token ? twoMinutesInMs : null);

  // return context with previously defined values
  return (
    <PocketContext.Provider
      value={{
        register,
        emailConfirmation,
        login,
        logout,
        recoverPassword,
        changePassword,
        user,
        token,
        session,
        pb,
      }}
    >
      {children}
    </PocketContext.Provider>
  );
};

// Create hook for ease of use
export const usePocketBase = () => useContext<PocketBaseContext>(PocketContext);
